import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate, useLocation } from "react-router-dom";
import * as z from "zod";
import { useAuth } from "./auth.context";

const formSchema = z.object({
  email: z.string().min(1, "Email is required").email("Invalid email format"),
  password: z.string().min(1, "Password is required"),
});

type SignInFormValues = z.infer<typeof formSchema>;

export default function SignIn() {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  // Extract redirect parameter from URL
  const searchParams = new URLSearchParams(location.search);
  const redirectTo = searchParams.get("redirect");

  // Helper function to validate redirect URL for security
  const validateRedirect = (redirect: string): boolean => {
    // Ensure it's a relative path starting with /
    if (!redirect.startsWith("/")) {
      return false;
    }

    try {
      // Validate that it forms a valid URL when combined with current origin
      // This catches malformed paths while ensuring same-origin
      const url = new URL(redirect, window.location.origin);
      return url.origin === window.location.origin;
    } catch {
      return false;
    }
  };

  const form = useForm<SignInFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  async function onSubmit(values: SignInFormValues) {
    setIsLoading(true);
    try {
      // Use the login function from auth context instead of direct fetch
      await login({
        email: values.email,
        password: values.password,
      });

      toast({
        title: "Success",
        description: "Signed in successfully. You will be redirected shortly.",
        variant: "success",
      });

      // Handle redirect logic
      if (redirectTo) {
        try {
          const decodedRedirectTo = decodeURIComponent(redirectTo);
          if (validateRedirect(decodedRedirectTo)) {
            // Redirect to the original intended destination
            navigate(decodedRedirectTo, { replace: true });
            return;
          }
        } catch (error) {
          // If decoding fails, fall through to default redirect
          console.warn("Failed to decode redirect URL:", redirectTo);
        }
      }

      // Default redirect to home page
      navigate("/", { replace: true });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description:
          error instanceof Error ? error.message : "Invalid credentials",
      });
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="w-full max-w-md space-y-8">
        <div className="text-center">
          <h2 className="font-display text-3xl font-bold text-foreground">
            Welcome Back
          </h2>
          <p className="mt-2 text-foreground">Sign in to your account</p>
        </div>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input type="email" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Password</FormLabel>
                  <FormControl>
                    <Input type="password" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button
              type="submit"
              className="w-full"
              disabled={isLoading}
              variant="default"
            >
              {isLoading ? "Signing in..." : "Sign In"}
            </Button>
          </form>
        </Form>

        <div className="space-y-4 text-center">
          <Link
            to="/request-password-reset"
            className="text-sm text-foreground hover:text-primary"
          >
            Forgot your password?
          </Link>
          <p className="text-sm text-muted-foreground">
            Don't have an account?{" "}
            <Link to="/signup" className="text-foreground hover:text-primary">
              Sign up
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
